import { STATUS_COLORS } from "constants/projectRules";

/**
 * Mã hành động phê duyệt (Approval Actions)
 */
export const APPROVAL_ACTIONS = {
  SUBMIT: "SUBMIT",
  RESUBMIT: "RESUBMIT",
  EDIT: "EDIT",
  APPROVE: "APPROVE",
  REQUEST_CHANGE: "REQUEST_CHANGE",
};

/**
 * Mã hành động thay đổi dữ liệu (Change Types) cho QĐGK và Hạng mục dự toán
 */
export const SUBCONTRACT_CHANGE_TYPES = {
  CREATE: "CREATE",
  UPDATE: "UPDATE",
  DELETE: "DELETE",
  STATUS_CHANGE: "STATUS_CHANGE",
  DOCUMENT_UPLOAD: "DOCUMENT_UPLOAD",
  DOCUMENT_DELETE: "DOCUMENT_DELETE",
  TEAM_ADD: "TEAM_ADD",
  TEAM_REMOVE: "TEAM_REMOVE",
  COMPLETION_UPDATE: "COMPLETION_UPDATE",
  CONVERT_TO_PARENT: "CONVERT_TO_PARENT",
  MOVE_ITEM: "MOVE_ITEM",
};

/**
 * Từ điển nhãn Tiếng Việt cho các mã hành động
 */
export const HISTORY_ACTION_LABELS = {
  // Phê duyệt
  SUBMIT: "Gửi phê duyệt",
  RESUBMIT: "Gửi lại phê duyệt",
  EDIT: "Chỉnh sửa hồ sơ",
  APPROVE: "Phê duyệt",
  REQUEST_CHANGE: "Yêu cầu chỉnh sửa",

  // Quyết định giao khoán & Dự toán
  CREATE: "Tạo mới",
  UPDATE: "Cập nhật",
  DELETE: "Xóa",
  STATUS_CHANGE: "Thay đổi trạng thái",
  DOCUMENT_UPLOAD: "Tải lên tài liệu",
  DOCUMENT_DELETE: "Xóa tài liệu",
  TEAM_ADD: "Thêm nhân sự",
  TEAM_REMOVE: "Xóa nhân sự",
  COMPLETION_UPDATE: "Cập nhật tiến độ",
  CONVERT_TO_PARENT: "Chuyển thành mục cha",
  MOVE_ITEM: "Di chuyển vị trí",
};

/**
 * Quy định màu sắc hiển thị cho các hành động dựa trên STATUS_COLORS của project
 */
export const HISTORY_ACTION_COLORS = {
  // Thành công / Phê duyệt / Tạo mới
  APPROVE: STATUS_COLORS.success,
  CREATE: STATUS_COLORS.success,

  // Chờ duyệt / Thay đổi trạng thái
  SUBMIT: STATUS_COLORS.warning,
  RESUBMIT: STATUS_COLORS.warning,
  STATUS_CHANGE: STATUS_COLORS.warning,

  // Cập nhật / Tải lên / Tiến độ
  UPDATE: STATUS_COLORS.info,
  DOCUMENT_UPLOAD: STATUS_COLORS.info,
  TEAM_ADD: STATUS_COLORS.info,
  COMPLETION_UPDATE: STATUS_COLORS.info,

  // Chỉnh sửa / Trả về / Phân cấp
  EDIT: STATUS_COLORS.purple,
  REQUEST_CHANGE: STATUS_COLORS.purple,
  CONVERT_TO_PARENT: STATUS_COLORS.purple,

  // Xóa / Hủy
  DELETE: STATUS_COLORS.danger,
  DOCUMENT_DELETE: STATUS_COLORS.danger,
  TEAM_REMOVE: STATUS_COLORS.danger,

  default: STATUS_COLORS.default,
};

/**
 * Lấy nhãn tiếng Việt cho hành động, có tính đến ngữ cảnh entityType
 */
export const getHistoryActionLabel = (actionOrChangeType, entityType = null) => {
  if (!actionOrChangeType) return "-";
  const key = String(actionOrChangeType).trim().toUpperCase();

  if (entityType) {
    const ent = String(entityType).trim().toUpperCase();
    if (ent === "DOCUMENT") {
      if (key === "CREATE" || key === "DOCUMENT_UPLOAD" || key === "UPLOAD") return "Tải lên tài liệu";
      if (key === "DELETE" || key === "DOCUMENT_DELETE") return "Xóa tài liệu";
    }
    if (ent === "TEAM") {
      if (key === "CREATE" || key === "TEAM_ADD" || key === "ADD") return "Thêm nhân sự";
      if (key === "DELETE" || key === "TEAM_REMOVE" || key === "REMOVE") return "Xóa nhân sự";
    }
    if (ent === "SUBCONTRACT") {
      if (key === "CREATE") return "Tạo mới giao khoán";
      if (key === "UPDATE") return "Cập nhật giao khoán";
    }
    if (ent === "ITEM" || ent === "SUBCONTRACT_ITEM") {
      if (key === "CREATE") return "Tạo mới hạng mục";
      if (key === "UPDATE") return "Cập nhật hạng mục";
      if (key === "DELETE") return "Xóa hạng mục";
    }
  }

  return HISTORY_ACTION_LABELS[key] || actionOrChangeType;
};

/**
 * Lấy style màu sắc cho hành động
 */
export const getHistoryActionStyle = (actionOrChangeType) => {
  if (!actionOrChangeType) return HISTORY_ACTION_COLORS.default;
  const key = String(actionOrChangeType).trim().toUpperCase();
  return HISTORY_ACTION_COLORS[key] || HISTORY_ACTION_COLORS.default;
};

const normalizeHistoryKey = (value) => String(value || "").trim().toLowerCase();
const fieldLabels = new Map(Object.entries({
  Personnel: "Nhân sự",
  Team: "Nhân sự",
  Document: "Tài liệu đính kèm",
  Subcontract: "Quyết định giao khoán",
  Item: "Dự toán",
  SubcontractItem: "Dự toán",
  SUBCONTRACT_ITEM: "Dự toán",
  SubcontractNumber: "Số quyết định giao khoán",
  SubcontractName: "Tên quyết định giao khoán",
  ContractId: "Hợp đồng chính",
  AssigneeName: "Đơn vị thực hiện",
  ProvinceId: "Tỉnh/Thành phố",
  SignDate: "Ngày ký",
  StartDate: "Ngày bắt đầu",
  EndDate: "Ngày kết thúc",
  StartDateAct: "Ngày bắt đầu thực tế",
  EndDateAct: "Ngày kết thúc thực tế",
  TotalValue: "Tổng giá trị quyết định giao khoán",
  Currency: "Đơn vị tiền tệ",
  Description: "Mô tả",
  Status: "Trạng thái",
  SubcontractFilePath: "File giao khoán",
  VAT: "VAT",
  ParentSubcontractId: "Giao khoán cha",
  PersonnelId: "Nhân sự",
  UserName: "Họ và tên",
  RoleInSubcontract: "Vai trò trong dự án",
  DisplayOrder: "STT",
  DocumentName: "Tên tài liệu",
  DocumentType: "Loại tài liệu",
}).map(([key, label]) => [normalizeHistoryKey(key), label]));

const itemFieldLabels = new Map(Object.entries({
  ItemCode: "STT",
  ItemName: "Dự toán",
  Unit: "ĐVT",
  ContractQuantity: "Khối lượng",
  SubcontractQuantity: "Khối lượng",
  ContractAmount: "Thành tiền",
  SubcontractAmount: "Thành tiền",
  UnitPrice: "Đơn giá sản phẩm",
  LaborCount: "Định biên",
  LaborNorm: "Định mức",
  LaborPrice: "Đơn giá công",
  DifficultyLevel: "Mức KK",
  Priority: "Độ ưu tiên",
  CompletionPercentage: "Tiến độ",
  Notes: "Ghi chú",
  StartDate: "Từ ngày",
  EndDate: "Đến ngày",
  ParentItemId: "Dự toán cha",
  DrafParentItemId: "Dự toán cha",
  OrderIndex: "Thứ tự",
  Level_: "Cấp độ",
  HasChildren: "Có dự toán con",
}).map(([key, label]) => [normalizeHistoryKey(key), label]));

// Map stored field keys to the current screen labels, including legacy history.
export const getHistoryFieldLabel = (history, context = "subcontract") => {
  const lookup = (value) => {
    const key = normalizeHistoryKey(value);
    return (context === "item" ? itemFieldLabels.get(key) : null) || fieldLabels.get(key);
  };
  const displayName = String(history?.fieldDisplayName || "").trim();
  const fieldName = String(history?.fieldName || "").trim();

  return lookup(fieldName) || lookup(displayName) || displayName || fieldName ||
    lookup(history?.entityType) || "-";
};
